import { PerformanceChartV2DataType } from './PerformanceChartV2DataType'

// Subset of useGameUpdatePerformanceData()'s return value that contributes
// timestamps to the per-axis minimum date calculation. Kept narrow so the
// helper can be unit-tested without depending on the hook itself.
export type PerformanceChartV2DataSeries = {
  estimates?: { ts: number }[]
  topGrossingRanks?: { ts: number }[]
  freeRanks?: { ts: number }[]
  dailyActiveUsers?: { date: number; activeUsers?: number | null }[]
  monthlyActiveUsers?: { date: number; activeUsers?: number | null }[]
  averageRevenuePerDailyActiveUser?: { date: number; value?: number | null }[]
}

/**
 * Active-users series (DAU / MAU / ARPDAU) are zero-padded back to the global
 * active-users floor (Jul 1 2024) for games released later: the API returns
 * entries with a 0 / null value for every period between the global floor and
 * the game's actual launch. Those leading zeros must NOT anchor the floor, so
 * we only consider dates whose value is a real (positive) number. (GR-2668)
 */
const timestampsWithData = (entries: { date: number; value?: number | null }[]): number[] =>
  entries.filter((entry) => (entry.value ?? 0) > 0).map((entry) => entry.date)

/**
 * Returns the earliest timestamp present in the data series that backs the
 * given data type so the date-range picker minDate (and the "All Available
 * Data" preset) anchor at the first date the selected series can actually
 * render.
 *
 * For Active Users (DAU / MAU / ARPDAU) we use ONLY the active-users series
 * — falling back to estimates would drag the floor down to whenever estimates
 * start (often before the active-users series begins), making the picker
 * disagree with the chart. We also skip the leading zero-valued entries the
 * API pads in for games released after the global active-users floor (see
 * timestampsWithData). We only fall back to estimates when the active-users
 * series has no real data at all so the picker still has a sensible floor.
 *
 * Returns Number.MAX_SAFE_INTEGER when no series has data so that a sibling
 * axis with data still wins via Math.min. (GR-2668)
 */
export const getMinDateForDataType = (data: PerformanceChartV2DataSeries, dataType: PerformanceChartV2DataType): number => {
  const estimateTimestamps = data.estimates?.map((estimate) => estimate.ts) ?? []
  let timestamps: number[] = []
  switch (dataType) {
    case PerformanceChartV2DataType.Revenue:
    case PerformanceChartV2DataType.Downloads:
    case PerformanceChartV2DataType.RevenueDownloadsRatio7Days:
    case PerformanceChartV2DataType.RevenueDownloadsRatio30Days:
    case PerformanceChartV2DataType.RevenueDownloadsRatio90Days:
    case PerformanceChartV2DataType.RevenueDownloadsRatioAllTime:
      timestamps = estimateTimestamps
      break
    case PerformanceChartV2DataType.Ranks:
      timestamps = [...(data.topGrossingRanks?.map((rank) => rank.ts) ?? []), ...(data.freeRanks?.map((rank) => rank.ts) ?? [])]
      break
    case PerformanceChartV2DataType.DAU: {
      const dauTimestamps = timestampsWithData((data.dailyActiveUsers ?? []).map((dau) => ({ date: dau.date, value: dau.activeUsers })))
      timestamps = dauTimestamps.length ? dauTimestamps : estimateTimestamps
      break
    }
    case PerformanceChartV2DataType.MAU: {
      const mauTimestamps = timestampsWithData((data.monthlyActiveUsers ?? []).map((mau) => ({ date: mau.date, value: mau.activeUsers })))
      timestamps = mauTimestamps.length ? mauTimestamps : estimateTimestamps
      break
    }
    case PerformanceChartV2DataType.ARPDAU: {
      const arpdauTimestamps = timestampsWithData(data.averageRevenuePerDailyActiveUser ?? [])
      timestamps = arpdauTimestamps.length ? arpdauTimestamps : estimateTimestamps
      break
    }
  }
  return timestamps.length ? Math.min(...timestamps) : Number.MAX_SAFE_INTEGER
}
